import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import SeoHead from "@/components/feature/SeoHead";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import CaseCard from "@/components/feature/CaseCard";
import { caseCategories, cases } from "@/mocks/cases";

const ALL = "全部";

const sortOptions = [
  { key: "heat", label: "热度优先" },
  { key: "newest", label: "最新归档" },
  { key: "oldest", label: "最早归档" },
];

const CasesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") ?? ALL;
  const sort = searchParams.get("sort") ?? "heat";
  const q = searchParams.get("q") ?? "";
  const [input, setInput] = useState(q);

  const update = (next: Record<string, string | null>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      if (value === null || value === "") params.delete(key);
      else params.set(key, value);
    });
    setSearchParams(params, { replace: true });
  };

  const filtered = useMemo(() => {
    const keyword = q.trim().toLowerCase();
    const list = cases.filter((item) => {
      const matchCategory = category === ALL || item.category === category;
      const matchKeyword =
        !keyword ||
        [item.name, item.handle, item.summary, item.category, ...item.tags]
          .join(" ")
          .toLowerCase()
          .includes(keyword);
      return matchCategory && matchKeyword;
    });

    return list.sort((a, b) => {
      if (sort === "newest") return a.fallDate < b.fallDate ? 1 : -1;
      if (sort === "oldest") return a.fallDate > b.fallDate ? 1 : -1;
      return b.heat - a.heat;
    });
  }, [category, q, sort]);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "塌房合集 - 网红塌房黑料合集",
      description:
        "按类型归档的网红塌房事件合集，涵盖虚假人设、言行打脸、抄袭搬运、带货翻车与数据注水。",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://hnhxedu.cn/" },
        { "@type": "ListItem", position: 2, name: "塌房合集", item: "https://hnhxedu.cn/cases" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: filtered.slice(0, 10).map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: `${item.name} 塌房档案`,
        url: `https://hnhxedu.cn/cases/${item.slug}`,
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SeoHead
        title="塌房合集 - 网红塌房旧料被扒与黑料合集归档"
        description="塌房合集收录已整理完成的网红塌房事件，支持按虚假人设、言行打脸、抄袭搬运、带货翻车、数据注水分类筛选，快速查看旧料被扒与历史爆料。"
        keywords="网红塌房,网红塌房黑料合集,网红塌房旧料被扒,塌房合集"
        path="/cases"
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main className="pt-16 md:pt-20">
        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100 border-b border-background-200/80">
          <div className="max-w-7xl mx-auto">
            <Breadcrumbs items={[{ label: "首页", href: "/" }, { label: "塌房合集" }]} />
            <h1 className="mt-5 font-heading font-black text-3xl md:text-5xl text-foreground-950">
              塌房合集
            </h1>
            <p className="mt-4 max-w-3xl text-sm md:text-base text-foreground-600 leading-relaxed">
              这里收录了档案局整理完成的全部塌房事件。每一起都配有旧料时间线、过往言论与争议焦点，
              你可以按类型筛选，也可以直接搜索想找的人或事件。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-8 md:py-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6 md:mb-8">
              <div className="flex flex-wrap gap-2 flex-1">
                <button
                  type="button"
                  onClick={() => update({ category: null })}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    category === ALL
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  全部
                </button>
                {caseCategories.map((cat) => (
                  <button
                    key={cat.key}
                    type="button"
                    onClick={() => update({ category: cat.key })}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                      category === cat.key
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 lg:w-auto">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base" />
                  <input
                    type="search"
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") update({ q: input });
                    }}
                    placeholder="搜索人物或关键词"
                    className="w-full sm:w-56 pl-9 pr-3 py-2 rounded-md text-sm bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => update({ q: input })}
                  className="px-4 py-2 rounded-md text-sm font-medium bg-secondary-500 text-background-50 hover:bg-secondary-600 transition-colors whitespace-nowrap"
                >
                  搜索
                </button>
                <select
                  value={sort}
                  onChange={(event) => update({ sort: event.target.value })}
                  className="px-3 py-2 rounded-md text-sm bg-background-50 border border-background-300 text-foreground-900 focus:outline-none focus:border-primary-400 cursor-pointer"
                  aria-label="排序方式"
                >
                  {sortOptions.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between mb-5 text-sm text-foreground-500">
              <p>
                共找到 <span className="font-bold text-primary-600">{filtered.length}</span> 起事件
                {category !== ALL && <span> · 分类「{category}」</span>}
                {q && <span> · 关键词「{q}」</span>}
              </p>
              {(category !== ALL || q || sort !== "heat") && (
                <button
                  type="button"
                  onClick={() => {
                    setInput("");
                    setSearchParams({}, { replace: true });
                  }}
                  className="inline-flex items-center gap-1 text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-refresh-line" />
                  重置
                </button>
              )}
            </div>

            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filtered.map((item, index) => (
                  <CaseCard key={item.id} item={item} rank={sort === "heat" ? index + 1 : undefined} />
                ))}
              </div>
            ) : (
              <div className="py-16 text-center bg-background-100 border border-dashed border-background-300 rounded-lg">
                <i className="ri-search-eye-line text-4xl text-foreground-400" />
                <h2 className="mt-4 font-heading font-bold text-lg text-foreground-950">
                  没有找到匹配的事件
                </h2>
                <p className="mt-2 text-sm text-foreground-600">
                  换个关键词试试，或者点击右上角把线索投给我们。
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setInput("");
                    setSearchParams({}, { replace: true });
                  }}
                  className="mt-5 px-5 py-2.5 rounded-md text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  查看全部事件
                </button>
              </div>
            )}
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
};

export default CasesPage;